import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CustomTabs from '../../../../components/custom-tabs/CustomTabs';
import { USER_DETAIL_PROFILE_TABS } from '../constants';

// Subcomponents
import BasicInfo from './profile/BasicInfo';
import Experience from './profile/Experience';
import Education from './profile/Education';
import Certifications from './profile/Certifications';
import Awards from './profile/Awards';
import Testimonials from './profile/Testimonials';
import Companies from './profile/Companies';
import Products from './profile/Products';
import MarketplaceListings from './profile/MarketplaceListings';
import Requirements from './profile/Requirements';
import WorkRegions from './profile/WorkRegions';
import ContactPurposes from './profile/ContactPurposes';

const Profile = ({ onEditAbout }) => {
    const [searchParams] = useSearchParams();
    const { userDetail } = useSelector((state) => state.allUsers);
    const activeProfileTab = searchParams.get("profile-tabs") || "basic-info";

    const renderProfileTabContent = (activeTab) => {
        switch (activeTab) {
            case "basic-info":
                return <BasicInfo userDetail={userDetail} onEditAbout={onEditAbout} />;
            case "experience":
                return <Experience userDetail={userDetail} />;
            case "education":
                return <Education userDetail={userDetail} />;
            case "certifications":
                return <Certifications userDetail={userDetail} />;
            case "awards":
                return <Awards userDetail={userDetail} />;
            case "testimonials":
                return <Testimonials userDetail={userDetail} />;
            case "companies":
                return <Companies userDetail={userDetail} />;
            case "products":
                return <Products userDetail={userDetail} />;
            case "marketplace-listings":
                return <MarketplaceListings userDetail={userDetail} />;
            case "requirements":
                return <Requirements userDetail={userDetail} />;
            case "work-regions":
                return <WorkRegions userDetail={userDetail} />;
            case "contact-purposes":
                return <ContactPurposes userDetail={userDetail} />;
            default:
                return <BasicInfo userDetail={userDetail} onEditAbout={onEditAbout} />;
        }
    };

    const completion = userDetail?.profile_completion?.percentage || 0;

    return (
        <div className='profile-tab grid gap-3'>
            <div className='bg-white rounded-3 g-col-2 p-3 shadow-md h-fit-content'>
                <h5 className='mb-2 fw-bold' style={{ fontSize: '16px' }}>Profile Sections</h5>
                <CustomTabs accessor='profile-tabs' className='flex-column gap-2' linkClassName="w-100 text-start" items={USER_DETAIL_PROFILE_TABS} defaultTab="basic-info" variant='pills' />
            </div>
            
            <div className='bg-white rounded-3 g-col-6 p-4 shadow-md h-fit-content'>
                {renderProfileTabContent(activeProfileTab)}
            </div>

            <div className="g-col-4">
                {/* Profile Completion Box */}
                <div className='bg-white rounded-3 mb-3 p-3 shadow-md'>
                    <h6 className="fw-bold mb-3" style={{ fontSize: '15px' }}>Profile Completion</h6>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="text-muted small fw-medium">Completion Rate</span>
                        <span className="fw-bold text-primary" style={{ fontSize: '14px' }}>{completion}%</span>
                    </div>
                    <div className="progress" style={{ height: "6px" }}>
                        <div 
                            className="progress-bar rounded-pill" 
                            role="progressbar" 
                            style={{ width: `${completion}%`, backgroundColor: '#7367f0' }} 
                            aria-valuenow={completion} 
                            aria-valuemin="0" 
                            aria-valuemax="100"
                        ></div>
                    </div>
                    <p className="text-muted small mt-2 mb-0" style={{ fontSize: '11px' }}>
                        {completion === 100 ? "Profile is fully complete!" : "Complete the profile sections to reach 100%."}
                    </p>
                </div>

                {/* Verification Summary Box */}
                <div className='bg-white rounded-3 mb-3 p-3 shadow-md user-details-page'>
                    <div className="overview-tab">
                        <h6 className="fw-bold mb-3" style={{ fontSize: '16px' }}>Verification Summary</h6>
                        <div className="verification-rows-container flex-grow-1">
                            <div className="verification-row py-2">
                                <span className="verification-label small">Email Verification</span>
                                <div className={`verification-value-wrapper ${userDetail?.is_email_verified ? 'text-success' : 'text-danger'}`}>
                                    <span className="fw-semibold me-1" style={{ fontSize: '12px' }}>
                                        {userDetail?.is_email_verified ? 'Verified' : 'Not Verified'}
                                    </span>
                                    {userDetail?.is_email_verified ? (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-success">
                                            <circle cx="12" cy="12" r="10" />
                                            <polyline points="16 9 11 14 8 11" />
                                        </svg>
                                    ) : (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-danger">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="15" y1="9" x2="9" y2="15" />
                                            <line x1="9" y1="9" x2="15" y2="15" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                            <div className="verification-row py-2">
                                <span className="verification-label small">Mobile Verification</span>
                                <div className={`verification-value-wrapper ${userDetail?.is_mobile_verified ? 'text-success' : 'text-danger'}`}>
                                    <span className="fw-semibold me-1" style={{ fontSize: '12px' }}>
                                        {userDetail?.is_mobile_verified ? 'Verified' : 'Not Verified'}
                                    </span>
                                    {userDetail?.is_mobile_verified ? (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-success">
                                            <circle cx="12" cy="12" r="10" />
                                            <polyline points="16 9 11 14 8 11" />
                                        </svg>
                                    ) : (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-danger">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="15" y1="9" x2="9" y2="15" />
                                            <line x1="9" y1="9" x2="15" y2="15" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                            <div className="verification-row py-2">
                                <span className="verification-label small">Identity Verification</span>
                                <div className={`verification-value-wrapper ${userDetail?.identity_verified ? 'text-success' : 'text-danger'}`}>
                                    <span className="fw-semibold me-1" style={{ fontSize: '12px' }}>
                                        {userDetail?.identity_verified ? 'Verified' : 'Not Verified'}
                                    </span>
                                    {userDetail?.identity_verified ? (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-success">
                                            <circle cx="12" cy="12" r="10" />
                                            <polyline points="16 9 11 14 8 11" />
                                        </svg>
                                    ) : (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-danger">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="15" y1="9" x2="9" y2="15" />
                                            <line x1="9" y1="9" x2="15" y2="15" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Profile;